import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthService } from '@/core/auth/auth.service';
import { PermissionService } from '@/core/permissions/permission.service';
import { UFS_REGIONAIS } from '@/shared/models/ufs';

/** O marcador de perfil nacional: quem tem esta "UF" não pertence a conselho regional nenhum. */
const UF_NACIONAL = 'BR';

/**
 * Quem é o usuário dentro do módulo de Serviços: de qual UF ele está vendo, e se ele só observa.
 *
 * <p>Antes deste store a UF era derivada em dois lugares (`servicos-content` e `dashboard-servicos`),
 * ambos com `uf || (isCfm ? 'BR' : '')`. Com a UF passando a vir também da URL, manter duas derivações
 * criaria a terceira divergência do módulo — a única fonte é parte do trabalho, não limpeza opcional.</p>
 *
 * <p><b>Para quem não é nacional a resolução é síncrona</b>, vinda do `auth`, sem HTTP. É requisito e
 * não detalhe: é o que impede a classe de bug em que a tela consulta com UF vazia durante uma janela
 * de carregamento e devolve nada, sem erro.</p>
 *
 * <p>Quem escreve aqui é só o `ServicosContent`, dono da rota do módulo.</p>
 */
@Injectable({ providedIn: 'root' })
export class UfEfetivaStore {
    private readonly auth = inject(AuthService);
    private readonly permissionService = inject(PermissionService);

    private readonly ufEscolhida = signal<string | null>(null);

    private readonly ufDoUsuario = computed(() => {
        const uf = this.auth.ufDoUsuario()?.trim().toUpperCase();
        // "Ausente" e sempre `null` no store (nunca `''`): sem usuario proprio e sem ser o nacional e
        // um caso de configuracao incompleta, nao o "sem escolha" do perfil nacional, mas a garantia de
        // representacao tem que valer para os dois.
        return uf || (this.permissionService.isCfm ? UF_NACIONAL : null);
    });

    /** Verdadeiro para o perfil nacional: ele acompanha o trabalho dos regionais, não o executa. */
    readonly somenteLeitura = computed(() => this.ufDoUsuario() === UF_NACIONAL);

    /**
     * A UF que o módulo está mostrando. Para regional é sempre a dele — o `?uf` da URL não o move,
     * porque a tela não deve anunciar uma UF que o servidor vai recusar.
     */
    readonly ufEfetiva = computed(() => (this.somenteLeitura() ? this.ufEscolhida() : this.ufDoUsuario()));

    /** "Ainda não escolheu" é diferente de "não há nada": a tela precisa dizer coisas diferentes. */
    readonly precisaEscolherUf = computed(() => this.somenteLeitura() && this.ufEfetiva() === null);

    definirUfEscolhida(uf: string | null): void {
        const normalizada = uf?.trim().toUpperCase();
        // Contra UFS_REGIONAIS, e nao so contra vazio: sem isto, `?uf=BR` reintroduz por outra porta o
        // bug que esta feature existe para eliminar (BR e truthy, e os relatorios/dashboard/consulta
        // passariam a consultar o backend com uf='BR'), e `?uf=XX` teria o mesmo efeito. UFS_REGIONAIS
        // deliberadamente nao contem 'BR', entao a validacao recusa os dois com a mesma lista.
        const valida = normalizada && UFS_REGIONAIS.some((regional) => regional.value === normalizada);
        this.ufEscolhida.set(valida ? normalizada : null);
    }
}
